import { set } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react'

import { Box, Button, Grid, Paper, Slider, Stack, Tooltip, Typography } from '@mui/material'
import { styled } from "@mui/system";
import { isEmptyOrUndefined } from '@zippeditoolsjs/blocks';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import ObstacleModal from './ObstacleModal';

const VerticalThumbSlider = styled(Slider, {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})(({ theme, isDisabled }) => ({
  height: 0,
  opacity: 1, // Always full opacity for the slider itself
  pointerEvents: 'auto', // Always auto for the slider itself, interaction managed by Paper

  "& .MuiSlider-thumb": {
    width: "4px",
    height: "40px",
    borderRadius: '2px',
    backgroundColor: isDisabled ? '#cccccc' : '#1a242d', // Gray thumb when disabled, dark when active
    "&:hover, &.Mui-active, &.Mui-focusVisible": {
      boxShadow: 'none',
    },
  },

  "& .MuiSlider-track": {
    display: 'none',
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: "-30px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "20px",
    height: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const formatPointTime = (timestamp) => timestamp?.split('T')[1]?.slice(0, 8) ?? '';

export default function NavigationTimeline(props) {
  const {
    t,
    storeMap,
    setPoses,
    obstacles,
    setObstacles,
    getObstacleThumbnails,
    isLoadingObstacleThumbnails,
    obstacleThumbnails,
    setSnackAlert,
    currentSessionObstaclesInfo,
    setCurrentSessionObstaclesInfo,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [obstacleIndex, setObstacleIndex] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null); // { sessionIndex, pointIndex } under the cursor
  const [timelineBySession, setTimelineBySession] = useState([]);

  useEffect(() => {
    if (!isEmptyOrUndefined(currentSessionObstaclesInfo, 'object') && !isEmptyOrUndefined(timelineBySession.length, 'array')) {
      const index = timelineBySession.findIndex(session => session.sessionName === currentSessionObstaclesInfo.sessionName);

      if (index !== -1) {
        setCurrentSession(index);
      }
    }
  }, [currentSessionObstaclesInfo?.sessionName])

  // Process all sessions from storeMap
  const processedSessions = useMemo(() => {
    if (!storeMap?.map_info?.obstacles_by_session) return [];

    const sessions = [];

    Object.entries(storeMap.map_info.obstacles_by_session).forEach(([sessionName, sessionData]) => {
      if (!sessionData?.poses || !sessionData?.obstacles) return;

      const posesWithType = sessionData.poses.map(pose => ({
        ...pose,
        type: "pose",
        unifiedTimestamp: Date.parse(pose.timestamp),
      }));

      const obstaclesWithType = sessionData.obstacles.map(obstacle => ({
        ...obstacle,
        type: "obstacle",
        unifiedTimestamp: Date.parse(obstacle.timestamp),
      }));

      const mergedList = [...posesWithType, ...obstaclesWithType].sort(
        (a, b) => a.unifiedTimestamp - b.unifiedTimestamp
      );

      sessions.push({
        sessionName,
        points: mergedList,
        maxValue: Math.max(0, mergedList.length - 1),
        value: 0,
        poses: sessionData.poses,
        obstacles: sessionData.obstacles,
        // day_obstacles and permanent_obstacles are passed here for setCurrentSessionObstaclesInfo for the SummaryCard
        day_obstacles: sessionData.day_obstacles,
        permanent_obstacles: sessionData.permanent_obstacles,
      });
    });

    return sessions;
  }, [storeMap]);

  // Update timelineBySession when processedSessions changes
  useEffect(() => {
    setTimelineBySession(processedSessions);
    if (processedSessions.length === 0) {
      setSnackAlert({
        open: true,
        message: t('cws_app.digital_store.no_data_timeline', 'No timeline data. Try a different date.'),
        severity: 'info'
      });
      setCurrentSession(null);
    } else {
      // Automatically select the first session
      setCurrentSession(0);
    }
  }, [processedSessions]);

  const handleModalOpen = (sessionIndex, pointIndex, obstacle) => {
    const session = timelineBySession[sessionIndex];
    if (!session) return;

    // If clicking an obstacle in a non-active session, make it active first
    if (currentSession !== sessionIndex) {
      setCurrentSession(sessionIndex);
      // Also, update the value for the newly active session
      const updatedSessions = timelineBySession.map((sess, idx) => ({
        ...sess,
        value: idx === sessionIndex ? pointIndex : 0 // Set initial value for new active session
      }));
      setTimelineBySession(updatedSessions);
    }

    const obstaclesWithType = session.obstacles.map(obstacle => ({
      ...obstacle,
      unifiedTimestamp: Date.parse(obstacle.timestamp),
    }));

    const obstacleIndexInSession = obstaclesWithType.findIndex(
      o => o.unifiedTimestamp === obstacle.unifiedTimestamp
    );

    // Only open the modal if the obstacle index or session has changed
    // And ensure the clicked session is the currentSession
    if ((obstacleIndex !== obstacleIndexInSession || currentSession !== sessionIndex) && currentSession === sessionIndex) {
      handleChange(sessionIndex, pointIndex, session.points[pointIndex]);
      setObstacleIndex(obstacleIndexInSession);
      setIsModalOpen(true);
    } else if (currentSession === sessionIndex) { // If already current session, just open modal if obstacle changed
      handleChange(sessionIndex, pointIndex, session.points[pointIndex]);
      setObstacleIndex(obstacleIndexInSession);
      setIsModalOpen(true);
    }
  };

  const handleChange = (sessionIndex, newValue, point) => {
    const session = timelineBySession[sessionIndex];
    if (!session) return;

    // Set the current active session if not already
    setCurrentSession(sessionIndex);

    let tempUnifiedTimestamp = null;
    let newValueIndex = null;
    let filteredPoints;

    if (point["unifiedTimestamp"] === undefined) {
      tempUnifiedTimestamp = Date.parse(point.timestamp);
      newValueIndex = session.points.findIndex(p =>
        p.unifiedTimestamp === tempUnifiedTimestamp && p?.type === "obstacle"
      );
    } else if (point?.["type"] !== undefined && point?.["type"] === "obstacle") {
      const obstaclesWithType = session.obstacles.map(obstacle => ({
        ...obstacle,
        unifiedTimestamp: Date.parse(obstacle.timestamp),
      }));
      const newIndex = obstaclesWithType.findIndex(
        o => o.unifiedTimestamp === session.points[newValue].unifiedTimestamp
      );
      setObstacleIndex(newIndex);
    }

    if (newValueIndex !== null) {
      filteredPoints = session.points.slice(0, newValueIndex + 1);
      newValue = newValueIndex;
    } else {
      filteredPoints = session.points.slice(0, newValue + 1);
    }

    // Update the session's value in timelineBySession and reset others
    const updatedSessions = timelineBySession.map((sess, index) => ({
      ...sess,
      value: index === sessionIndex ? newValue : 0
    }));
    setTimelineBySession(updatedSessions);

    const filteredPoses = filteredPoints.filter(p => p?.type === "pose");
    const filteredObstacles = filteredPoints.filter(p => p?.type === "obstacle");

    setPoses(filteredPoses);
    setObstacles(filteredObstacles);
  };

  // Maps the cursor position to the closest point, using the same scale as the slider thumb
  const handleTimelineHover = (event, sessionIndex, session) => {
    if (isEmptyOrUndefined(session.points, 'array')) return;

    const { left, width } = event.currentTarget.getBoundingClientRect();
    if (!width) return;

    const ratio = Math.min(Math.max((event.clientX - left) / width, 0), 1);
    const pointIndex = Math.round(ratio * session.maxValue);

    // Keeps the same state object while the cursor stays on the same point, to avoid rerenders
    setHoveredPoint(previous => (
      previous?.sessionIndex === sessionIndex && previous?.pointIndex === pointIndex
        ? previous
        : { sessionIndex, pointIndex }
    ));
  };

  const renderSessionSlider = (session, sessionIndex) => {
    const isActiveSession = currentSession === sessionIndex;
    const isDisabledSession = currentSession !== null && !isActiveSession;
    const hoveredIndex = hoveredPoint?.sessionIndex === sessionIndex ? hoveredPoint.pointIndex : null;
    const hoveredTime = hoveredIndex !== null ? formatPointTime(session.points[hoveredIndex]?.timestamp) : '';
    const hoveredPosition = hoveredIndex !== null && session.maxValue > 0
      ? (hoveredIndex / session.maxValue) * 100
      : null;

    return (
      <Box key={session.sessionName} sx={{ position: 'relative' }}>
        <Typography>{t('cws_app.digital_store.session', 'Session')} {sessionIndex + 1}</Typography>
        <Paper
          key={session.sessionName}
          onMouseMove={(event) => handleTimelineHover(event, sessionIndex, session)}
          onMouseLeave={() => setHoveredPoint(null)}
          // This onClick makes the whole Paper component clickable to activate the session
          onClick={() => {
            setCurrentSession(sessionIndex);
            setCurrentSessionObstaclesInfo({
              sessionName: session.sessionName,
              poses: session.poses,
              obstacles: session.obstacles,
              day_obstacles: session.day_obstacles,
              permanent_obstacles: session.permanent_obstacles,
            });
          }}
          sx={{
            height: '100%',
            position: 'relative',
            backgroundColor: isDisabledSession ? '#f8f8f8' : 'white', // Lighter gray for disabled
            transition: 'all 0.2s ease-in-out', // Smooth transitions for all properties
            cursor: 'pointer', // Indicate clickable
            overflow: 'hidden', // Ensures content stays within bounds, especially important for the graying filter

            // Hover effects for the entire Paper
            '&:hover': {
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)', // Shadow for hover
            },

            // Visual graying for disabled state
            filter: isDisabledSession ? 'grayscale(0.8)' : 'none', // Stronger grayscale
          }}
        >
          {/* Hover guide marking the point the time tag refers to */}
          {hoveredPosition !== null &&
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: `${hoveredPosition}%`,
                width: '1px',
                height: '100%',
                backgroundColor: 'rgba(26, 36, 45, 0.35)',
                pointerEvents: 'none',
                zIndex: 50,
              }}
            />
          }

          {/* Draw obstacles in slider */}
          <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex" }}>
            {Array.isArray(session.points) &&
              session.points.map((item, pointIndex) => {
                const length = session.points?.length;
                // Avoid division by zero if length is 0
                const normalizedX = length > 0 ? (pointIndex / length) * 100 : 0;

                // Tooltip open only for the active session and when not in modal
                let isTooltipOpen = pointIndex === session.value && isActiveSession && !isModalOpen;

                return (
                  (item?.type === "obstacle" ?
                    <div
                      key={pointIndex}
                      // Allow clicking obstacles regardless of current session status.
                      // The handleModalOpen logic will activate the session.
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent Paper's onClick from firing
                        handleModalOpen(sessionIndex, pointIndex, item);
                      }}
                      style={{
                        position: "absolute",
                        left: `${normalizedX}%`,
                        minWidth: "1px",
                        height: "100%",
                        backgroundColor: item.permanent ? "#9d03fd" : "#fd6303",
                        padding: 0,
                        zIndex: 100,
                        cursor: 'pointer', // Always pointer for clickable obstacles
                        opacity: 1, // Obstacle itself should not be grayed out directly by this div
                      }}
                    >
                      <Tooltip
                        title={<IconComponent iconName='warning-outline' sx={{ fontSize: '60px' }} />}
                        key={`${sessionIndex}-${pointIndex}`}
                        placement="top"
                        open={isTooltipOpen}
                        arrow
                        slotProps={{
                          popper: { sx: { cursor: "pointer" } }
                        }}
                      >
                        <div
                          style={{
                            // This inner div makes the clickable area for the tooltip, matching the obstacle line
                            position: "absolute",
                            left: 0, // Relative to its parent obstacle div
                            width: "1px", // The actual visual obstacle line
                            height: "100%",
                            backgroundColor: 'transparent', // Make this div transparent, the parent div provides color
                            pointerEvents: "auto"
                          }}
                        />
                      </Tooltip>
                    </div>
                    :
                    null
                  )
                );
              })}
          </div>

          {/* Slider */}
          <VerticalThumbSlider
            // Slider is only truly disabled if there are no points.
            // Otherwise, its interaction is managed by whether the parent Paper is active.
            disabled={isEmptyOrUndefined(session.points, 'array')}
            isDisabled={isDisabledSession} // Pass isDisabled to style the thumb
            min={0}
            max={session.maxValue}
            value={session.value}
            // Value label only on for active session and specific conditions
            valueLabelDisplay={isActiveSession && (session.points[session.value]?.type === "pose" || isModalOpen) ? 'on' : 'off'}
            valueLabelFormat={(val) => formatPointTime(session.points[val]?.timestamp) || val}
            onChange={(event, newValue) => {
              // Only allow slider interaction if it's the active session
              if (isActiveSession) {
                handleChange(sessionIndex, newValue, session.points[newValue]);
              }
            }}
            // Prevent pointer events on the slider if it's not the active session
            // This works in conjunction with the Paper's onClick to manage interaction.
            sx={{ pointerEvents: isActiveSession ? 'auto' : 'none' }}
          />
        </Paper>

        {/* Time of the hovered point, tracking the guide line. It lives outside the Paper, which clips its content */}
        {hoveredPosition !== null && hoveredTime &&
          <Typography
            variant='caption'
            sx={{
              position: 'absolute',
              top: 0,
              left: `${hoveredPosition}%`,
              transform: 'translateX(-50%)',
              px: 0.75,
              borderRadius: '4px',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              backgroundColor: '#1a242d',
              color: 'primary.contrastText',
              zIndex: 20,
            }}
          >
            {hoveredTime}
          </Typography>
        }
      </Box>
    );
  };

  return (
    <Grid
      item
      sx={{
        width: '100%',
        position: 'sticky',
        bottom: 0,
        zIndex: 10,
        paddingTop: '8px !important',
        pb: 1,
        backgroundColor: 'background.default',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.12)',
      }}
    >
      <Stack spacing={2}>
        {timelineBySession.map((session, sessionIndex) =>
          renderSessionSlider(session, sessionIndex)
        )}
      </Stack>

      {/* Modal */}
      {isModalOpen && obstacles && (obstacleIndex !== null) && currentSession !== null ?
        <ObstacleModal
          t={t}
          handleChange={(event, newValue) => handleChange(currentSession, newValue, timelineBySession[currentSession]?.points[newValue])}
          getObstacleThumbnails={getObstacleThumbnails}
          isLoadingObstacleThumbnails={isLoadingObstacleThumbnails}
          obstacleThumbnails={obstacleThumbnails}
          setIsModalOpen={setIsModalOpen}
          isModalOpen={isModalOpen}
          value={obstacleIndex}
          currentSessionObstaclesInfo={timelineBySession[currentSession]}
        />
        : null}
    </Grid>
  );
};